import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useClients, useInvalidate } from '../hooks/useApi';
import api from '../services/api';
import toast from 'react-hot-toast';
import { PlusIcon, MagnifyingGlassIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

export default function Clients() {
  const [search, setSearch] = useState('');
  const { data: clients, isLoading } = useClients(search);
  const { invalidateClients } = useInvalidate();
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [form, setForm] = useState({ code: '', name: '', phone: '', email: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await api.put(`/clients/${editingClient.id}`, form);
        toast.success('Klienti u përditësua!');
      } else {
        await api.post('/clients', form);
        toast.success('Klienti u shtua!');
      }
      setShowModal(false);
      setEditingClient(null);
      setForm({ code: '', name: '', phone: '', email: '' });
      invalidateClients();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gabim');
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setForm({ code: client.code, name: client.name, phone: client.phone || '', email: client.email || '' });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Jeni i sigurt?')) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success('Klienti u fshi!');
      invalidateClients();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gabim');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Klientët</h1>
        <button onClick={() => { setEditingClient(null); setForm({ code: '', name: '', phone: '', email: '' }); setShowModal(true); }} className="btn-primary flex items-center gap-2">
          <PlusIcon className="w-5 h-5" /> Shto Klient
        </button>
      </div>

      <div className="card">
        <div className="relative mb-4">
          <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Kërko klient..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-field pl-10"
          />
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-2 font-medium text-gray-500">Kodi</th>
                  <th className="text-left py-3 px-2 font-medium text-gray-500">Emri</th>
                  <th className="text-left py-3 px-2 font-medium text-gray-500">Tel</th>
                  <th className="text-right py-3 px-2 font-medium text-gray-500">Bilanci</th>
                  <th className="text-right py-3 px-2 font-medium text-gray-500">Veprime</th>
                </tr>
              </thead>
              <tbody>
                {clients?.map((client) => (
                  <tr key={client.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-2 font-medium">{client.code}</td>
                    <td className="py-3 px-2">
                      <Link to={`/clients/${client.id}`} className="text-primary-600 hover:underline">
                        {client.name}
                      </Link>
                    </td>
                    <td className="py-3 px-2 text-gray-500">{client.phone}</td>
                    <td className={`py-3 px-2 text-right font-medium ${client.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      €{client.balance?.toFixed(2)}
                    </td>
                    <td className="py-3 px-2 text-right">
                      <button onClick={() => handleEdit(client)} className="p-1 text-gray-500 hover:text-primary-600">
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      <button onClick={() => handleDelete(client.id)} className="p-1 text-gray-500 hover:text-red-600 ml-1">
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">{editingClient ? 'Ndrysho Klient' : 'Shto Klient'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Kodi</label><input type="text" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="input-field" required /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Emri</label><input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-field" required /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label><input type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-field" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Email</label><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-field" /></div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Anulo</button>
                <button type="submit" className="btn-primary flex-1">Ruaj</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
